import React, { MouseEvent, useState } from 'react';
import { styled } from '../../styles';
import { BpDesignSystem } from '../../types';
import { Button, type ButtonProps } from '../Button';
import { Icon } from '../Icon';
import { Menu } from '../Menu';

type BaseProps<T extends string> = BpDesignSystem.Props<{
  options: { label: string; value: T }[] | undefined;
  placeholder?: string;
  dataTest?: string;
  disabled?: boolean;
  menuWidth?: number | 'trigger-width';
  menuPlacement?: 'left' | 'center' | 'right';
  variant?: ButtonProps['variant'];
  clearable?: boolean;
}>;

export type SelectInlineProps<T extends string> = BaseProps<T> &
  (
    | {
        multiple: true;
        values: T[];
        onChange: (values: T[]) => void;
      }
    | {
        multiple?: false;
        value: T | null | undefined;
        onChange: (values: T | undefined) => void;
      }
  );

export const SelectInline = <T extends string>({
  options,
  dataTest = 'select-inline',
  placeholder = '',
  disabled,
  style,
  sx,
  className,
  menuWidth,
  variant = 'text',
  menuPlacement = 'left',
  clearable,
  ...rest
}: SelectInlineProps<T>) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [anchorWidth, setAnchorWidth] = useState<number | undefined>(undefined);

  const open = Boolean(anchorEl);
  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    if (menuWidth === 'trigger-width') {
      setAnchorWidth(event.currentTarget.offsetWidth);
    }
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (value: T) => {
    if (rest.multiple) {
      if (rest.values.includes(value)) {
        rest.onChange(rest.values.filter((it) => it !== value));
      } else {
        rest.onChange([...rest.values, value]);
      }
    } else {
      rest.onChange(value);
      handleClose();
    }
  };

  let displayValue: string | undefined;

  if ('values' in rest) {
    displayValue =
      rest.values?.length > 0
        ? rest.values.map((it) => options?.find((option) => option.value === it)?.label).join('/')
        : (placeholder ?? '');
  } else {
    displayValue = rest.value
      ? (options?.find((option) => option.value === rest.value)?.label ?? placeholder ?? '')
      : (placeholder ?? '');
  }

  const hasValue = rest.multiple ? rest.values?.length > 0 : !!rest.value;

  const endIcon =
    clearable && hasValue ? (
      <Icon
        name="clear"
        size="small"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          rest.multiple ? rest.onChange([]) : rest.onChange(undefined);
        }}
      />
    ) : open ? (
      'arrow_drop_up'
    ) : (
      'arrow_drop_down'
    );

  return (
    <>
      <StyledButton
        id="select-inline-button"
        aria-controls={open ? 'select-inline-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        variant={variant}
        disabled={disabled}
        size="small"
        dataTest={dataTest}
        endIcon={endIcon}
        className={className}
        style={style}
        sx={sx}
      >
        {displayValue}
      </StyledButton>
      <Menu
        id="basic-menu"
        dataTest={`${dataTest}-menu`}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: menuPlacement
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: menuPlacement
        }}
        MenuListProps={{
          'aria-labelledby': 'select-inline-button',
          style: {
            width: typeof menuWidth === 'number' ? menuWidth : menuWidth === 'trigger-width' ? anchorWidth : undefined
          }
        }}
      >
        {options?.map((option) => {
          const isSelected = rest.multiple
            ? !!rest.values.find((value) => value === option.value)
            : rest.value === option.value;

          return (
            <Menu.Item selected={isSelected} key={option.value} onClick={() => handleSelect(option.value)}>
              {option.label}
            </Menu.Item>
          );
        })}
      </Menu>
    </>
  );
};

const StyledButton = styled(Button)`
    && {
        border-radius: ${({ theme }) => theme.borderRadius.xl};
    }
`;
